import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Collapse,
  Divider,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';

import SecurityIcon from '@mui/icons-material/Security';
import ArticleIcon from '@mui/icons-material/Article';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PhoneIcon from '@mui/icons-material/Phone';
import HistoryIcon from '@mui/icons-material/History';

import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AndroidIcon from '@mui/icons-material/Android';
import FaceRetouchingOffIcon from '@mui/icons-material/FaceRetouchingOff';
import HelpIcon from '@mui/icons-material/Help';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';

interface SidebarProps {
  drawerWidth: number;
  mobileOpen: boolean;
  onDrawerToggle: () => void;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  { text: '대시보드', icon: <DashboardIcon />, path: '/dashboard' },
  { text: '사용자 관리', icon: <PeopleIcon />, path: '/users' },
  {
    text: '신고 관리',
    icon: <PhoneIcon />,
    children: [
      { text: '전화번호 신고', icon: <PhoneIcon />, path: '/reports/phone' },
      { text: '주간 보안 리포트', icon: <AssessmentIcon />, path: '/reports/weekly' },
      { text: '이탈 분석', icon: <AssessmentIcon />, path: '/reports/churn' },
    ],
  },
  {
    text: '보안 관리',
    icon: <SecurityIcon />,
    children: [
      { text: '악성 앱 블랙리스트', icon: <BlockIcon />, path: '/security/blacklist' },
      { text: '안전 앱 화이트리스트', icon: <CheckCircleIcon />, path: '/security/whitelist' },
      { text: '악성 앱 스캔', icon: <AndroidIcon />, path: '/security/virus-scan' },
      { text: '유저 스캔 내역', icon: <HistoryIcon />, path: '/security/user-scan-history' },
      { text: '스팸 번호 관리', icon: <PhoneIcon />, path: '/security/spam-phones' },
      { text: 'APK 분석', icon: <AndroidIcon />, path: '/security/apk-analysis' },
      { text: '딥페이크 분석', icon: <FaceRetouchingOffIcon />, path: '/security/deepfake-analysis' },
    ],
  },
  {
    text: '콘텐츠 관리',
    icon: <ArticleIcon />,
    children: [
      { text: 'FAQ', icon: <HelpIcon />, path: '/content/faq' },
      { text: '공지사항', icon: <AnnouncementIcon />, path: '/content/notice' },
      { text: '소식 관리', icon: <ArticleIcon />, path: '/content/news' },
      { text: '공식 연락처', icon: <ContactPhoneIcon />, path: '/content/contacts' },
    ],
  },
  { text: '푸시 알림', icon: <NotificationsIcon />, path: '/notifications' },
];

const Sidebar: React.FC<SidebarProps> = ({ drawerWidth, mobileOpen, onDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

  const handleMenuClick = (item: MenuItem) => {
    if (item.children) {
      setOpenMenus(prev => ({ ...prev, [item.text]: !prev[item.text] }));
    } else if (item.path) {
      navigate(item.path);
      if (mobileOpen) onDrawerToggle();
    }
  };

  const isSelected = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isParentSelected = (item: MenuItem) => {
    if (item.path) return isSelected(item.path);
    if (item.children) {
      return item.children.some(child => isSelected(child.path));
    }
    return false;
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => (
    <React.Fragment key={item.text}>
      <ListItem disablePadding>
        <ListItemButton
          selected={isSelected(item.path) || (level === 0 && isParentSelected(item))}
          onClick={() => handleMenuClick(item)}
          sx={{ pl: level * 2 + 2 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
          {item.children && (openMenus[item.text] ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </ListItem>
      {item.children && (
        <Collapse in={openMenus[item.text]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map(child => renderMenuItem(child, level + 1))}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'primary.main' }}>
          Dr.Phishing
        </Box>
      </Toolbar>
      <Divider />
      <List>{menuItems.map(item => renderMenuItem(item))}</List>
    </div>
  );

  return (
    <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
